import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { getMemberByUser } from '@/lib/memberLookup';

export function useGroupLeaderPermission() {
  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => base44.auth.me()
  });

  const { data: permissions } = useQuery({
    queryKey: ['permissions', user?.email],
    queryFn: async () => {
      if (!user?.email) return null;
      const byUserEmail = await base44.entities.AdminPermission.filter({ user_email: user.email });
      if (byUserEmail.length > 0) return byUserEmail[0];
      const byEmail = await base44.entities.AdminPermission.filter({ email: user.email });
      return byEmail[0] || null;
    },
    enabled: !!user?.email
  });

  const { data: leaderGroups = [] } = useQuery({
    queryKey: ['leaderGroups', user?.email],
    queryFn: async () => {
      if (!user?.email) return [];
      const groups = await base44.entities.Group.list();
      return groups.filter(group => 
        group.leaders?.some(leader => leader.leader_id === user.email || leader.leader_name === user.full_name)
      );
    },
    enabled: !!user?.email
  });

  const { data: teamLeaderships = [] } = useQuery({
    queryKey: ['teamLeaderships', user?.email],
    queryFn: async () => {
      if (!user?.email) return [];
      const leaders = await base44.entities.Leader.filter({ is_active: true });
      return leaders.filter(leader => 
        leader.members?.some(member => {
          const linkedMember = leader.members.find(m => m.member_id === member.member_id);
          // Verifica se o usuário está vinculado como membro desta liderança
          const memberData = base44.entities.Member.filter({ id: member.member_id, email: user.email });
          return memberData.then(m => m.length > 0);
        })
      );
    },
    enabled: !!user?.email
  });

  const { data: currentMember } = useQuery({
    queryKey: ['currentMember', user?.id, user?.email],
    queryFn: () => getMemberByUser(user),
    enabled: !!user
  });

  const { data: leadershipsByMember = [] } = useQuery({
    queryKey: ['leadershipsByMember', currentMember?.id],
    queryFn: async () => {
      if (!currentMember?.id) return [];
      const leaders = await base44.entities.Leader.filter({ is_active: true });
      return leaders.filter(leader => 
        leader.members?.some(m => m.member_id === currentMember.id)
      );
    },
    enabled: !!currentMember?.id
  });

  const isAdmin = user?.role === 'admin' || permissions?.is_super_admin;
  const isGroupLeader = leaderGroups.length > 0;
  const leaderGroupIds = leaderGroups.map(g => g.id);

  // Verificar se é líder de equipe de louvor
  const isWorshipLeader = leadershipsByMember.some(leader => 
    leader.members?.some(m => 
      m.member_id === currentMember?.id && m.link_to_worship === true
    )
  );

  // Verificar se é líder de equipe de mídia
  const isMediaLeader = leadershipsByMember.some(leader => 
    leader.members?.some(m => 
      m.member_id === currentMember?.id && m.link_to_media === true
    )
  );

  const isDanceLeader = leadershipsByMember.some(leader =>
    leader.members?.some(m =>
      m.member_id === currentMember?.id && m.link_to_dance === true
    )
  );

  // Verificar se é líder de equipe de recepção
  const isReceptionLeader = leadershipsByMember.some(leader => 
    leader.members?.some(m => 
      m.member_id === currentMember?.id && m.link_to_reception === true
    )
  );

  // Verificar se é líder de equipe Kids MBV
  const isKidsLeader = leadershipsByMember.some(leader => 
    leader.members?.some(m => 
      m.member_id === currentMember?.id && m.link_to_kids === true
    )
  );

  return {
    user,
    permissions,
    isAdmin,
    isGroupLeader,
    leaderGroups,
    leaderGroupIds,
    canManage: isAdmin || isGroupLeader,
    isWorshipLeader,
    isMediaLeader,
    isDanceLeader,
    isReceptionLeader,
    isKidsLeader,
    canManageWorship: isAdmin || isWorshipLeader,
    canManageMedia: isAdmin || isMediaLeader,
    canManageDance: isAdmin || isDanceLeader,
    canManageReception: isAdmin || isReceptionLeader,
    canManageKids: isAdmin || isKidsLeader
  };
}
